import React, { useState, memo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card } from '../components/ui/Card'
import { DataTable, StatusBadge } from '../components/ui'
import { PropertyPanel } from '../components/ui/PropertyPanel'
import { TYPE_NAMES_RU } from '../components/canvas/equipmentTypes'
import { usePlacement, Device, Recommendation } from '../hooks/usePlacement'

const typeIcons: Record<string, string> = {
  server: '🖥️',
  switch: '🔀',
  storage: '💾',
  firewall: '🛡️',
  router: '🌐',
  pdu: '⚡',
  ups: '🔋',
  generic: '📦',
}

const typeColors: Record<string, string> = {
  server: 'text-blue-400',
  switch: 'text-green-400',
  storage: 'text-purple-400',
  firewall: 'text-red-400',
  router: 'text-yellow-400',
  pdu: 'text-orange-400',
  ups: 'text-cyan-400',
  generic: 'text-gray-400',
}

function confidenceBadge(c: number): 'critical' | 'warning' | 'normal' {
  if (c >= 0.9) return 'normal'
  if (c >= 0.7) return 'warning'
  return 'critical'
}

/* ====================================================================== */
/*  Components                                                            */
/* ====================================================================== */

const DeviceList = memo(function DeviceList({ 
  devices, 
  addDevice, 
  removeDevice 
}: { 
  devices: Device[]; 
  addDevice: (d: Device) => void;
  removeDevice: (i: number) => void;
}) {
  const [form, setForm] = useState<Omit<Device, 'name'>>({
    type: 'server',
    u_height: 1,
    weight_kg: 10,
    power_watts: 100,
  })
  const [newName, setNewName] = useState('')

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    const name = newName.trim() || `Device ${devices.length + 1}`
    addDevice({ name, ...form })
    setNewName('')
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleAdd} className="space-y-3 p-3 bg-bg-input/30 border border-border/50 rounded-sm">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">
          Новое устройство
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <label className="text-xs text-text-muted">Имя устройства</label>
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Например, Server-01"
              className="w-full bg-bg-input border border-border rounded-sm px-3 py-1.5 text-sm outline-none focus:border-accent"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-text-muted">Тип</label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full bg-bg-input border border-border rounded-sm px-3 py-1.5 text-sm outline-none focus:border-accent"
            >
              <option value="server">Сервер</option>
              <option value="switch">Коммутатор</option>
              <option value="storage">Хранилище</option>
              <option value="firewall">Межсетевой экран</option>
              <option value="router">Маршрутизатор</option>
              <option value="pdu">PDU</option>
              <option value="ups">ИБП</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-text-muted">Высота (U)</label>
            <input
              type="number"
              value={form.u_height}
              onChange={e => setForm(f => ({ ...f, u_height: parseInt(e.target.value) || 1 }))}
              className="w-full bg-bg-input border border-border rounded-sm px-3 py-1.5 text-sm outline-none focus:border-accent"
              min={1}
              max={42}
            />
          </div>
        </div>
        <button
          type="submit"
          className="w-full bg-accent text-white py-2 rounded-sm text-sm font-medium hover:bg-accent-hover transition-colors mt-2"
        >
          Добавить в список
        </button>
      </form>

      {devices.length > 0 && (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg-input">
                <th className="px-3 py-2 text-left text-xs text-text-muted">Имя</th>
                <th className="px-3 py-2 text-left text-xs text-text-muted">Тип</th>
                <th className="px-3 py-2 text-right text-xs text-text-muted">U</th>
                <th className="px-3 py-2 text-right text-xs text-text-muted">Вт</th>
                <th className="px-3 py-2 text-center text-xs text-text-muted">Действия</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="px-3 py-2">{d.name}</td>
                  <td className={`px-3 py-2 ${typeColors[d.type] || 'text-gray-400'}`}>
                    {typeIcons[d.type] || '📦'} {TYPE_NAMES_RU[d.type] || d.type}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{d.u_height}</td>
                  <td className="px-3 py-2 text-right font-mono">{d.power_watts}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => removeDevice(i)} className="text-status-critical hover:underline">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
})

const RecommendationsTable = memo(function RecommendationsTable({
  recommendations,
  loading,
  onRowClick,
}: {
  recommendations: Recommendation[]
  loading: boolean
  onRowClick?: (row: Recommendation) => void
}) {
  if (loading) return <div className="p-4 text-text-muted text-sm">Расчёт рекомендаций...</div>

  const columns = [
    { key: 'device_name' as const, label: 'Устройство' },
    { key: 'rack_label' as const, label: 'Стойка' },
    { key: 'u_position' as const, label: 'U-позиция' },
    { key: 'u_height' as const, label: 'Высота U' },
    { 
      key: 'confidence' as const, 
      label: 'Уверенность',
      render: (v: number) => (
        <StatusBadge 
          status={confidenceBadge(v)} 
          label={`${(v * 100).toFixed(0)}%`} 
        />
      )
    },
    { key: 'reason' as const, label: 'Причина' },
  ]

  return (
    <DataTable<Recommendation>
      columns={columns}
      data={recommendations}
      loading={false}
      onRowClick={onRowClick}
      emptyText="Нет рекомендаций — добавьте устройства для размещения"
    />
  )
})

/* ====================================================================== */
/*  Main Page                                                             */
/* ====================================================================== */

export default function PlacementPage() {
  const { dcCode: urlDcCode } = useParams<{ dcCode: string }>()
  const navigate = useNavigate()
  
  const {
    dcCode,
    setDcCode,
    dcList,
    devices,
    addDevice,
    removeDevice,
    recommendations,
    result,
    loading,
    calculatePlacement,
    applyPlacement,
  } = usePlacement(urlDcCode || '')

  const [selectedDevice, setSelectedDevice] = useState<Recommendation | null>(null)

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Размещение оборудования</h1>
          <p className="text-text-secondary text-sm mt-1">
            Автоматический расчёт оптимального размещения устройств по стойкам
          </p>
        </div>
        <select
          value={dcCode}
          onChange={e => {
            const code = e.target.value
            setDcCode(code)
            navigate(`/${code}/placement`)
          }}
          className="text-sm bg-bg-input border border-border rounded-sm px-3 py-1.5"
        >
          {dcList.map(dc => (
            <option key={dc.code} value={dc.code}>{dc.name || dc.code}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 flex gap-4 p-6 overflow-auto">
        {/* Left: device list */}
        <div className="w-[420px] shrink-0 flex flex-col">
          <Card title="Список устройств">
            <DeviceList devices={devices} addDevice={addDevice} removeDevice={removeDevice} />
          </Card>

          <div className="mt-4">
            <button
              onClick={calculatePlacement}
              disabled={devices.length === 0 || loading}
              className={`w-full py-3 rounded-sm text-sm font-medium transition-colors ${
                devices.length === 0 || loading
                  ? 'bg-bg-input text-text-muted cursor-not-allowed'
                  : 'bg-accent text-white hover:bg-accent-hover'
              }`}
            >
              {loading ? 'Расчёт...' : `Рассчитать (${devices.length} устройств)`}
            </button>
          </div>

          {result && (
            <Card className="mt-4" title="Итог">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-text-muted">Всего</div>
                  <div className="text-2xl font-mono">{result.total_devices}</div>
                </div>
                <div>
                  <div className="text-text-muted">Размещено</div>
                  <div className="text-2xl font-mono text-status-normal">{result.placed_devices}</div>
                </div>
                <div>
                  <div className="text-text-muted">Не размещено</div>
                  <div className="text-2xl font-mono text-status-critical">{result.failed_devices.length}</div>
                </div>
                <div>
                  <div className="text-text-muted">Всего кВт</div>
                  <div className="text-2xl font-mono">{(result.placed_devices * 0.1).toFixed(1)}</div>
                </div>
              </div>
              {result.failed_devices.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-xs text-status-critical mb-1">Причины отказов:</div>
                  {result.failed_devices.slice(0, 3).map((f, i) => (
                    <div key={i} className="text-xs text-text-secondary">• {f.reason}</div>
                  ))}
                </div>
              )}
              {result.placed_devices > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <button
                    onClick={applyPlacement}
                    className="w-full bg-status-normal text-white py-2 rounded-sm text-sm font-medium hover:bg-green-600 transition-colors"
                  >
                    Применить размещение
                  </button>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Right: recommendations */}
        <div className="flex-1 flex flex-col min-w-0">
          <Card title="Рекомендации">
            <RecommendationsTable 
              recommendations={recommendations} 
              loading={loading} 
              onRowClick={setSelectedDevice} 
            />
          </Card>
        </div>
      </div>

      {/* Detail panel */}
      {selectedDevice && (
        <PropertyPanel
          title="Детали размещения"
          isOpen={!!selectedDevice}
          onClose={() => setSelectedDevice(null)}
        >
          <dl className="space-y-2 text-sm">
            <div><dt className="text-text-muted">Устройство</dt><dd className="font-mono">{selectedDevice.device_name}</dd></div>
            <div><dt className="text-text-muted">Стойка</dt><dd className="font-mono">{selectedDevice.rack_label}</dd></div>
            <div><dt className="text-text-muted">U-позиция</dt><dd className="font-mono">{selectedDevice.u_position}</dd></div>
            <div><dt className="text-text-muted">Уверенность</dt><dd><StatusBadge status={confidenceBadge(selectedDevice.confidence)} label={`${(selectedDevice.confidence * 100).toFixed(0)}%`} /></dd></div>
            <div><dt className="text-text-muted">Причина</dt><dd>{selectedDevice.reason}</dd></div>
          </dl>
        </PropertyPanel>
      )}
    </div>
  )
}
